import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { OAHelper } from '@app/api/core/utils/helper/offerteantraege-helper';
import { PolizzechangeDialogComponent } from '@app/shared/components/dialog/beratungchange-dialog/polizzechange-dialog.component';
import { DataService } from '@app/shared/services/data/data.service';
import { UserTextService } from '@app/shared/services/data/user-text.service';
import { UserService } from '@app/shared/services/data/user.service';
import { PeziService } from '@app/shared/services/pezi.service';
import { RouterService } from '@app/shared/services/router.service';
import { LowLevelApi } from '@app/shared/services/state/low-level-api.service';
import { ActiveTab } from '@app/shared/services/state/model/o30/activetab';
import { DialogService } from '@app/shared/services/ui/dialog/dialog.service';
import { Action, DialogResult, DialogStatus } from '@app/shared/services/ui/dialog/dialog.types';
import { DisplayOption, MessageType } from '@app/shared/services/ui/message/message';
import { MessageService } from '@app/shared/services/ui/message/message.service';
import { ReactiveDialogService } from '@app/shared/services/ui/reactive-dialog.service';
import { ExpandableRow, ExpandableRowBase, ExpandedRowAddition, ListData, ListTyp, OfferteAntraegeTableColumn, OffertStageTwoRowAddition } from '@app/views/vertriebscockpit/offerteantraege/offerteantraege.types';
import { FieldProperty, FreieBemerkung, Payload, Person, PolizzenStatus, PolizzeResult } from '@o30/entities';
import { BehaviorSubject, combineLatest, EMPTY, firstValueFrom, forkJoin, Observable, of, of as observableOf } from 'rxjs';
import { first, map, switchMap, take } from 'rxjs/operators';

// TODO refactor, this doesnt need to be a component (make getAnzRows... abstract)
@Component({
  selector:    'app-basic-polizze-list',
  templateUrl: './basic-polizze-list.component.html',
  standalone:  true
})
export class BasicPolizzeListComponent implements OnInit,
                                                  OnDestroy {
  protected llapi = inject(LowLevelApi);
  protected dataService = inject(DataService);
  protected messageService = inject(MessageService);
  protected dialogService = inject(DialogService);
  protected routerService = inject(RouterService);
  protected reactiveDialogService = inject(ReactiveDialogService);
  protected userService = inject(UserService);
  protected userTextService = inject(UserTextService);
  protected destroyRef = inject(DestroyRef);
  protected peziService = inject(PeziService);

  // pagination
  paginator: MatPaginator;
  currentPaginationLabel = '';
  readonly isMakler = signal(false);

  isNonExpandableDetailRow = (i: number, row: Object) => row.hasOwnProperty('detailRow');

  @ViewChild(MatPaginator, {static: true}) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
  }

  selectedKunde: Person;
  bearbeitenErlaubt = false;
  @Input()
  iddBefreit: boolean;
  @Input()
  listData: ListData;
  @Input()
  filterChangedEvent: Observable<void>;

  @Input()
  markedPolintSubject: BehaviorSubject<number>;

  tableColumns: OfferteAntraegeTableColumn[] = [];

  constructor() {

    this.tableColumns.push(...OAHelper.defaultTableColumns);
    this.tableColumns.push(...this.getAdditionalColumns());
    this.userService.isAuthorized$('O30_WRITE')
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(_hasRight => {
        if (_hasRight) {
          this.bearbeitenErlaubt = true;
        }
      });
    this.userService.isAuthorized$('PZ_MAKLER')
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(_hasRight => this.isMakler.set(_hasRight));
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--initialisation--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  public loadList(): void {
    this.listData.reloadListSubject.next(null);
  }

  private initPaginator(): void {
    this.listData.dataSource.paginator = this.paginator;
    this.updateLabel();
  }

  public updateLabel(): void {
    this.currentPaginationLabel = OAHelper.getPaginationLabel(this.paginator.pageIndex, this.listData.dataSource.filteredData.length, this.getAnzRowsPerEntry(), this.getEntriesPerPage());
  }

  public updateLabelAndGoToPage(pageIndex: number): void {
    if (this.paginator) {
      this.paginator.pageIndex = pageIndex;
      // Bug in Material angular - ohne diese Zeile wird die pagination nicht refreshed
      this.paginator._changePageSize(this.paginator.pageSize);
    }
    this.updateLabel();
  }

  getAnzRowsPerEntry(): number {
    throw new Error('implement getAnzRowsPerEntry !!');
  }

  getEntriesPerPage(): number {
    return 10;
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--lifecycle hooks--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  ngOnInit(): void {
    this.initPaginator();
    combineLatest([
        this.listData.loadingFinished,
        this.markedPolintSubject?.asObservable() || observableOf(null)
      ]
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([finished, markedPolint]) => {
        if (markedPolint && finished) {
          const index = this.listData.dataSource.data.findIndex(row => row.data.id === markedPolint) / this.getAnzRowsPerEntry();
          if (index >= 0) {
            const page = Math.trunc(index / this.getEntriesPerPage());
            this.updateLabelAndGoToPage(page);
          }
        }
      });

    this.filterChangedEvent.pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(data => {
          this.updateLabelAndGoToPage(0);
        }
      );

    this.listData.dataSource.connect()
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        data => {
          this.updateLabel();
        }
      );
  }

  ngOnDestroy(): void {
    // Nach Verlassen des tabs, die row nicht mehr markieren
    if (this.markedPolintSubject &&
      this.listData.dataSource.data &&
      this.listData.dataSource.data.findIndex(element => element.data.id === this.markedPolintSubject.value) >= 0) {
      // reset markedPolint, wenn die seite verlassen wird und das element auf der aktuellen vorhanden war
      this.markedPolintSubject.next(null);
    }
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--list handling--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  isExpansionDetailRow = (i: number, row: ExpandableRow<PolizzeResult> | ExpandedRowAddition<PolizzeResult>) => row.hasOwnProperty('expandedRow');

  public toggleExpanded(row: { data: { id: number; }; isExpanded: boolean; }): void {
    const exIndex = this.listData.dataSource.data.findIndex(
      exrow => exrow.hasOwnProperty('expandedRow') &&
        (exrow as ExpandedRowAddition<PolizzeResult>).expandedRow &&
        (exrow as ExpandedRowAddition<PolizzeResult>).data.id === row.data.id);
    if (exIndex !== -1 && this.listData.dataSource.data[exIndex].hasOwnProperty('isExpanded')) {
      (this.listData.dataSource.data[exIndex] as ExpandableRowBase<PolizzeResult>).isExpanded = !row.isExpanded;
      row.isExpanded = !row.isExpanded;
    }
  }

  public sortData(sort: Sort): void {
    if (this.listData?.dataSource && sort.active && sort.direction) {
      const isAsc = sort.direction === 'asc';
      this.listData.dataSource.data.sort((a, b) => {
        if (a.data.id === b.data.id) { // Zwei Rows mit selben Wert nicht sortieren → Expandable Rows gelten als eigene Row, gehören aber zu einem zusammengesetzten Datensatz dazu
          return 0;
        }
        if (sort.active === 'vkeId') {
          return this.compare((a.data['vke']).id, (b.data['vke']).id, sort.direction === 'asc');
        }
        if (typeof a.data[sort.active] === 'string') {
          return this.compare((a.data[sort.active] as string).toLowerCase(), (b.data[sort.active] as string).toLowerCase(), sort.direction === 'asc');
        }
        if (a.data[sort.active] && b.data[sort.active]) {
          return this.compare(a.data[sort.active], b.data[sort.active], isAsc);
        }
        if (a.data[sort.active] && !b.data[sort.active]) {
          return isAsc ? 1 : -1;
        }
        if (!a.data[sort.active] && b.data[sort.active]) {
          return isAsc ? -1 : 1;
        }
        return this.compare(a.data[sort.active], b.data[sort.active], isAsc);
      });
      // this statement seems redundant
      // but re-assigning dataSource.data is necessary for changes to be rendered
      this.listData.dataSource.data = this.listData.dataSource.data;
    }
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--common actions--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  public editPolizze(row: PolizzeResult): void {
    this.routerService.routeOffertKundenInfoStart(row.vke.id, row.id);
  }

  async openInPezi(row: PolizzeResult) {
    if(this.isMakler()){
      await this.peziService.openPeziExternerMakler(`maklerpolizze/${row.id}`);
    } else{
      this.peziService.openPeziWithLogin(`/polizze/${row.id}`, this.destroyRef);
    }
  }

  public createAndArchiveOffert(row: PolizzeResult): void {
    this.llapi.patchValue('layout.spinner', true);
    this.dataService.createAndArchiveOffert(row.id)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        this.llapi.patchValue('layout.spinner', false);
        this.messageService.handlePayload(result, {displayOption: DisplayOption.SNACKBAR});
      });
  }

  public deletePolizze(row: PolizzeResult, title: string): void {
    this.messageService.showSimpleYesNoDialog(
        `Möchten Sie die Auswahl wirklich löschen?`,
        title
      )
      .afterClosed()
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(dialogResult => {
        if (dialogResult?.status === 'yes') {
          this.dataService.deletePolizze(row.id)
            .subscribe(payload => {
                if (this.messageService.handlePayloadReturnSuccess(payload)) {
                  this.removeFromList(row.id);
                }
              }
            );
        }
      });
  }

  /**
   * removes up to 3 table rows per entry
   * usually they will be 1 or 2 because "expandedRow" and "detailRow" are currently toggleable
   * @param polizzeId
   */
  protected removeFromList(polizzeId: number): void {
    const data = this.listData.dataSource.data;
    // remove actual entry
    data.splice(this.listData.dataSource.data.findIndex(value => value.hasOwnProperty('data') &&
      (value as ExpandableRow<PolizzeResult>).data.id === polizzeId), 1);

    // remove expanded entry
    const expandedIndex = this.listData.dataSource.data.findIndex(value => value.hasOwnProperty('expandedRow') &&
      (value as ExpandedRowAddition<PolizzeResult>).data.id === polizzeId);
    if (expandedIndex !== -1) {
      data.splice(expandedIndex, 1);
    }

    // remove detail entry
    const detailIndex = this.listData.dataSource.data.findIndex(value => value.hasOwnProperty('detailRow') &&
      (value as OffertStageTwoRowAddition<PolizzeResult>).data.id === polizzeId);
    if (detailIndex !== -1) {
      data.splice(detailIndex, 1);
    }

    this.listData.dataSource.data = data;

    // refresh paginator
    this.updateLabel();
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--resets--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  isStornoBeantragt(polizzeResult: PolizzeResult): boolean {
    return polizzeResult.status === PolizzenStatus.STORNO_BEANTRAGT;
  }

  async resetToOffertStageOne(row: PolizzeResult): Promise<void> {
    const config = {
      data: {
        buttonConfig: {
          visible:       true,
          actionButtons: [
            {
              name:  'Auf Offert zurücksetzen',
              color: 'accent',
              value: Action.RESET
            },
            {
              name:  'Als Genehmigung speichern',
              color: 'accent',
              value: Action.SAVE
            },
            {

              name:  'Abbrechen',
              color: 'restrained',
              value: Action.CLOSE
            }
          ]
        }
      }
    } as MatDialogConfig;

    const isPolizzeResetable$ = this.dataService.checkPolizzeForReset(row.id);
    const isPolizzeResetable = await firstValueFrom(isPolizzeResetable$);

    if (isPolizzeResetable?.data) {
      const alwaysTrue = (_) => of({data: true} as Payload<boolean>);
      this.basicReset(row, 'Wollen Sie die Auswahl auf den Offertstatus zurücksetzen?',
        alwaysTrue,
        this.dataService.savePolizzeOffert2Reset.bind(this.dataService),
        ListTyp.OFFERT, ActiveTab.OfferteAntraege_OFFERTE);
    } else {
      // Nicht direkt zurücksetzbar → daher Fragen ob zur Genehmigung oder direkt auf Offert
      const answerResetOrGenehmigung$ = this.messageService.showDialog({
          text: 'Wollen Sie die Auswahl auf den Offertstatus zurücksetzen oder erneut zur Genehmigung senden?',
          icon: 'help_outline'
        }, config)
        .afterClosed()
        .pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef));
      const answerResetOrGenehmigung = await firstValueFrom(answerResetOrGenehmigung$);

      if (answerResetOrGenehmigung.status === Action.RESET) { // direkt
        this.forceReset(row.id);
      } else if (answerResetOrGenehmigung.status === Action.SAVE) { // genehmigung
        // don't check if approval is required anymore
        // since the user can decide to reset directly
        const alwaysFalse = (_) => of({data: false} as Payload<boolean>);
        this.basicReset(row, 'Wollen Sie die Auswahl auf den Offertstatus zurücksetzen?',
          alwaysFalse, // this.dataService.checkPolizzeForReset.bind(this.dataService),
          this.dataService.savePolizzeOffert2Reset.bind(this.dataService),
          ListTyp.OFFERT, ActiveTab.OfferteAntraege_OFFERTE);
      }
    }
  }

  private forceReset(polizzeId: number) {
    this.dataService.savePolizzeOffert2ResetForce(polizzeId, true, null)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(_payload => {
        if (this.messageService.handlePayloadReturnSuccess(_payload, {
          displayOption: DisplayOption.MONITOR
        })) {
          this.listData.reloadListSubject.next(ListTyp.OFFERT);
          this.llapi.patchValue('layout.activeOfferteAntraegeTab', ActiveTab.OfferteAntraege_OFFERTE);
          this.markedPolintSubject.next(_payload.data);

          this.loadList();
          this.messageService.showSimpleOkDialog('Polizze erfolgreich zurückgesetzt', MessageType.INFO);
        }
      });
  }

  protected basicReset(row: PolizzeResult, textConfirm: string,
                       checkCallback: (id: number) => Observable<Payload<boolean>>,
                       saveCallback: (id: number, b: FreieBemerkung) => Observable<Payload<number>>,
                       addionaltReloadTab?: ListTyp,
                       changeToTabOnSuccess?: ActiveTab): void {
    checkCallback(row.id)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        map(_checkPayload => !_checkPayload.data),
        switchMap(_requiresApproval =>
          _requiresApproval
            ? this.dialogService.displayFreieBemerkungDialog(null, false, 'Erneutes Genehmigungsansuchen an VT')
              .afterClosed()
            : this.getResetConfirmDialog(textConfirm)
        ),
        switchMap(_dialogResult => (_dialogResult.statusId !== DialogStatus.CANCELLED)
          ? observableOf(_dialogResult)
          : EMPTY
        ),
        switchMap(_dialogResult => saveCallback(row.id, _dialogResult.data))
      )
      .subscribe(_savePayload => {
        if (this.messageService.handlePayloadReturnSuccess(_savePayload, {
          displayOption: DisplayOption.MONITOR
        })) {
          if (this.markedPolintSubject) {
            this.markedPolintSubject.next(_savePayload.data);
          }
          if (addionaltReloadTab) {
            this.listData.reloadListSubject.next(addionaltReloadTab);
          }
          if (changeToTabOnSuccess) {
            this.llapi.patchValue('layout.activeOfferteAntraegeTab', changeToTabOnSuccess);
          }
        }
        this.loadList(); // liste neu laden
      });
  }

  private getResetConfirmDialog(text: string): Observable<DialogResult<FreieBemerkung>> {
    return this.messageService.showSimpleOkCancelDialog(text, 'Aktion bestätigen')
      .afterClosed()
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        switchMap(_dialogResult => {
          return (_dialogResult?.status === Action.OK)
            ? observableOf({
              data:   this.defaultBemerkung,
              status: Action.SAVE
            } as DialogResult<FreieBemerkung>)
            : EMPTY;
        }));
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--"abstract" methods--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  protected get displayedColumns(): string[] {
    throw new Error('displayedColumns not implemented');
  }

  protected getAdditionalColumns(): OfferteAntraegeTableColumn[] {
    return [];
  }

  private get defaultBemerkung(): FreieBemerkung {
    return {
      text: {value: 'Kein Grund angegeben'} as FieldProperty<string>
    } as FreieBemerkung;
  }

  polizzeChange(row: PolizzeResult, typvar: PolizzechangeDialogComponent['typvar']): void {
    forkJoin([
      this.dataService.getBeratungChange(row.id),
      this.userTextService.getOffertStage2Bezeichnung$()
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef), take(1),
        switchMap(([_payload, _bezl]) => {
          const bezl = typvar === 'beratung' ? _bezl : 'Antrag';
          const title = `${bezl} bearbeiten`;
          const config = {
            data:         {
              title
            },
            disableClose: true,
            maxWidth:     '800px'
          };
          return this.reactiveDialogService.open(PolizzechangeDialogComponent, config, _payload.data, {
              polizzeId:       row.id,
              vkeId:           row.vke.id,
              ersterKundeId:   row.personId,
              isKonvertierung: row.konvertierung,
              typvar:          typvar
            })
            .afterClosed();
        })
      )
      .subscribe(_payload => {
        if (_payload?.status !== 'cancelled') {
          this.loadList();
        }
      });
  }
}
